/**
 * Client body-measurements list. Ported from the original app's
 * profile/measurements/page.tsx — same UI verbatim, with next/link → react-router
 * Link, useRouter → useNavigate, and the api client.
 * Reads GET /api/measurements, deletes via DELETE /api/measurements/:id.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, Plus, Trash2, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';

interface Measurement {
  id: string;
  date: string;
  weight: number | null;
  body_fat_pct: number | null;
  chest: number | null;
  waist: number | null;
  hips: number | null;
  source: string;
  // DEXA composition fields (present on source==='dexa' rows)
  fat_mass: number | null;
  lean_mass: number | null;
  bone_density: number | null;
  visceral_fat: number | null;
  trunk_fat_pct: number | null;
  ag_ratio: number | null;
}

export default function Measurements() {
  const navigate = useNavigate();
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ measurements?: Measurement[] }>('/api/measurements')
      .then((d) => setMeasurements(d.measurements || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleDelete(id: string) {
    try {
      await api.del(`/api/measurements/${id}`);
      setMeasurements((prev) => prev.filter((m) => m.id !== id));
    } catch {
      /* */
    }
  }

  const latest = measurements[0];
  const prev = measurements[1];

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--gold)' }}>
            <ArrowLeft size={22} />
          </button>
          <h1 className="display" style={{ fontSize: "1.9rem" }}>Measurements</h1>
        </div>
        <Link to="/profile/measurements/add" className="btn-gold text-sm py-2 px-3 flex items-center gap-1">
          <Plus size={14} /> Add
        </Link>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2].map((i) => <div key={i} className="card animate-pulse h-20" style={{ background: 'var(--surface)' }} />)}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Mini Charts */}
          {latest && (
            <div className="grid grid-cols-2 gap-3">
              <div className="card text-center">
                <p className="text-xs" style={{ color: 'var(--gray-500)' }}>Weight</p>
                <p className="text-2xl mt-1" style={{ fontFamily: "var(--font-display), Georgia, serif", lineHeight: 1.05, color: "var(--cream)" }}>{latest.weight || '--'}</p>
                {prev?.weight && latest.weight && (
                  <p className="text-xs mt-1 flex items-center justify-center gap-0.5" style={{ color: latest.weight < prev.weight ? 'var(--success)' : 'var(--error)' }}>
                    <TrendingUp size={10} /> {(latest.weight - prev.weight).toFixed(1)} lbs
                  </p>
                )}
              </div>
              <div className="card text-center">
                <p className="text-xs" style={{ color: 'var(--gray-500)' }}>Body Fat</p>
                <p className="text-2xl mt-1" style={{ fontFamily: "var(--font-display), Georgia, serif", lineHeight: 1.05, color: "var(--cream)" }}>{latest.body_fat_pct ? `${latest.body_fat_pct}%` : '--'}</p>
              </div>
            </div>
          )}

          {/* Measurement History */}
          {measurements.length === 0 ? (
            <div className="card text-center py-8">
              <p className="text-white font-semibold">No measurements yet</p>
              <p className="text-xs mt-1" style={{ color: 'var(--gray-400)' }}>Track your body composition over time</p>
            </div>
          ) : (
            measurements.map((m) => (
              <div key={m.id} className="card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">{formatDate(m.date)}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs" style={{ color: 'var(--gray-400)' }}>
                      {m.weight != null && <span>Weight: {m.weight} lbs</span>}
                      {m.body_fat_pct != null && <span>BF: {m.body_fat_pct}%</span>}
                      {m.source === 'dexa' ? (
                        <>
                          {m.fat_mass != null && <span>Fat: {m.fat_mass} lbs</span>}
                          {m.lean_mass != null && <span>Lean: {m.lean_mass} lbs</span>}
                          {m.visceral_fat != null && <span>VAT: {m.visceral_fat}</span>}
                          {m.trunk_fat_pct != null && <span>Trunk: {m.trunk_fat_pct}%</span>}
                          {m.ag_ratio != null && <span>A/G: {m.ag_ratio}</span>}
                          {m.bone_density != null && <span>BMD: {m.bone_density}</span>}
                        </>
                      ) : (
                        <>
                          {m.chest != null && <span>Chest: {m.chest}&quot;</span>}
                          {m.waist != null && <span>Waist: {m.waist}&quot;</span>}
                          {m.hips != null && <span>Hips: {m.hips}&quot;</span>}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--surface)', color: 'var(--gray-500)' }}>
                      {m.source}
                    </span>
                    <button onClick={() => handleDelete(m.id)} style={{ color: 'var(--gray-500)' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(date: string): string {
  return new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
